import {
  type FC,
  type KeyboardEvent,
  type RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { truthyPredicate } from '@iconik-io/core/src/truthy_predicate';
import { useDebounce } from '@iconik-io/shared-lib/src/react/hooks/use_debounce';
import { Icon, Popover, ProgressCircle, Separator } from '@iconik-io/uad';

import { INPUT_DEBOUNCE_TIMEOUT } from '../../../../constants/debounce';
import type { MultiSelectFilterItemDisplay } from './types';
import { getInitials } from './utils';

import styles from './multi_select_filter_selection_popover.module.css';

type Props = {
  triggerRef: RefObject<HTMLElement | null>;
  selectedItemIds: string[];
  resolvedItems: Map<string, MultiSelectFilterItemDisplay>;
  loadOptions: (query: string) => Promise<MultiSelectFilterItemDisplay[]>;
  initialOptions?: MultiSelectFilterItemDisplay[];
  onSelectionChange: (selectedIds: string[]) => void;
  onClose: () => void;
  showItemAvatar?: boolean;
  showStartTypingHint?: boolean;
  /** Empty-query hint shown when `showStartTypingHint` is set. */
  startTypingHint?: string;
  matchTriggerWidth?: boolean;
};

export const MultiSelectFilterSelectionPopover: FC<Props> = ({
  triggerRef,
  selectedItemIds,
  resolvedItems,
  loadOptions,
  initialOptions,
  onSelectionChange,
  onClose,
  showItemAvatar = true,
  showStartTypingHint = false,
  startTypingHint = 'Start typing to search or add a value',
  matchTriggerWidth = true,
}) => {
  const [query, setQuery] = useState('');
  const isPreloaded = initialOptions !== undefined;
  const [options, setOptions] = useState<MultiSelectFilterItemDisplay[]>(
    initialOptions ?? [],
  );
  const [isLoading, setIsLoading] = useState(!isPreloaded);
  const [loadError, setLoadError] = useState<unknown>(null);
  // The query the current `options` reflect, so an empty result can be told
  // apart from a fetch that hasn't run yet (e.g. during the input debounce).
  const [fetchedQuery, setFetchedQuery] = useState<string | null>(
    isPreloaded ? '' : null,
  );
  const searchInputRef = useRef<HTMLInputElement>(null);
  const requestIndexRef = useRef(0);
  const selectedSet = useMemo(
    () => new Set(selectedItemIds),
    [selectedItemIds],
  );

  const fetchOptions = useCallback(
    async (searchQuery: string) => {
      const requestIndex = ++requestIndexRef.current;
      setIsLoading(true);
      setLoadError(null);
      try {
        const result = await loadOptions(searchQuery);
        if (requestIndex === requestIndexRef.current) {
          setOptions(result);
          setFetchedQuery(searchQuery);
        }
      } catch (error) {
        if (requestIndex === requestIndexRef.current) {
          setLoadError(error);
        }
      } finally {
        if (requestIndex === requestIndexRef.current) {
          setIsLoading(false);
        }
      }
    },
    [loadOptions],
  );

  const fetchOptionsDebounced = useDebounce(
    fetchOptions,
    INPUT_DEBOUNCE_TIMEOUT,
    [fetchOptions],
  );

  const queryRef = useRef(query);
  useEffect(() => {
    queryRef.current = query;
  }, [query]);

  // `loadOptions` — and with it `fetchOptions` — gets a new identity whenever
  // the selection changes, which re-runs this effect. Refetch the query that is
  // in the search box rather than an empty one, so checking an item doesn't
  // replace the visible options with the empty-query result.
  useEffect(() => {
    if (!isPreloaded) {
      fetchOptions(queryRef.current);
    }
  }, [fetchOptions, isPreloaded]);

  useEffect(() => {
    searchInputRef.current?.focus();
  }, []);

  const handleToggleItem = (itemId: string) => {
    if (selectedSet.has(itemId)) {
      onSelectionChange(selectedItemIds.filter((id) => id !== itemId));
    } else {
      onSelectionChange([...selectedItemIds, itemId]);
    }
  };

  const hasActiveQuery = query.trim().length > 0;

  const isQuerySettled =
    fetchedQuery !== null && fetchedQuery.trim() === query.trim();

  if (loadError) {
    throw loadError;
  }

  const creatableItems = options.filter(
    (option) => option.isCreatable && !selectedSet.has(option.id),
  );
  // Treat a creatable item as an existing option once it has been selected so
  // it moves to the checked section and remains visible in the dropdown.
  const existingOptions = options.filter(
    (option) => !option.isCreatable || selectedSet.has(option.id),
  );
  const selectedOptions = existingOptions.filter((option) =>
    selectedSet.has(option.id),
  );
  const unselectedOptions = existingOptions.filter(
    (option) => !selectedSet.has(option.id),
  );

  const selectedItems = hasActiveQuery
    ? selectedOptions
    : selectedItemIds
        .map((id) => resolvedItems.get(id))
        .filter(truthyPredicate);

  // Pressing Enter commits the top option for the current query — the creatable
  // value if one is offered, otherwise the first matching existing option — and
  // closes the popover, so a term can be added without reaching for the mouse.
  const handleInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') {
      return;
    }
    event.preventDefault();
    if (!hasActiveQuery) {
      return;
    }
    const target = creatableItems[0] ?? unselectedOptions[0];
    if (!target) {
      return;
    }
    if (!selectedSet.has(target.id)) {
      onSelectionChange([...selectedItemIds, target.id]);
    }
    onClose();
  };

  const renderOptionItem = (
    option: MultiSelectFilterItemDisplay,
    isSelected: boolean,
  ) => (
    <button
      key={option.id}
      type="button"
      className={styles.option_item}
      onClick={() => handleToggleItem(option.id)}
      role="checkbox"
      aria-checked={isSelected}
    >
      <span
        className={`${styles.checkbox}${isSelected ? ` ${styles.checked}` : ''}`}
      >
        {isSelected && <Icon name="checkmark" size="2xs" />}
      </span>
      {option.imageUrl ? (
        <img
          src={option.imageUrl}
          alt=""
          className={
            option.imageType === 'logo'
              ? styles.option_logo
              : styles.option_avatar
          }
        />
      ) : null}
      {!option.imageUrl && showItemAvatar ? (
        <span className={styles.option_initials} data-ui="option-initials">
          {getInitials(option.label)}
        </span>
      ) : null}
      <span className={styles.option_label}>{option.label}</span>
    </button>
  );

  return (
    <Popover
      isOpen
      triggerRef={triggerRef}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
      placement="bottom start"
      // This cross offset is a workaround to prevent overlaying
      // the trigger element when the subtitle is shown
      crossOffset={selectedItemIds.length > 0 ? 0.01 : 0}
      className={styles.popover}
      style={
        matchTriggerWidth
          ? { width: triggerRef.current?.offsetWidth }
          : undefined
      }
    >
      <div className={styles.content}>
        <div className={styles.search_container}>
          <input
            ref={searchInputRef}
            type="text"
            className={styles.search_input}
            placeholder="Search..."
            value={query}
            onChange={(event) => {
              const newQuery = event.target.value;
              setQuery(newQuery);
              fetchOptionsDebounced(newQuery);
            }}
            onKeyDown={handleInputKeyDown}
          />
        </div>
        <div className={styles.options_list} role="group" aria-label="Options">
          {isLoading && (
            <div className={styles.loading_state}>
              <ProgressCircle isIndeterminate aria-label="Loading options" />
            </div>
          )}
          {!isLoading && (
            <>
              {creatableItems.map((item) => renderOptionItem(item, false))}
              {creatableItems.length > 0 &&
                (selectedItems.length > 0 || unselectedOptions.length > 0) && (
                  <Separator variant="sub" />
                )}
              {selectedItems.length > 0 && (
                <>
                  {selectedItems.map((item) => renderOptionItem(item, true))}
                  {unselectedOptions.length > 0 && <Separator variant="sub" />}
                </>
              )}
              {unselectedOptions.map((item) => renderOptionItem(item, false))}
              {creatableItems.length === 0 &&
                selectedItems.length === 0 &&
                unselectedOptions.length === 0 &&
                (showStartTypingHint && hasActiveQuery && !isQuerySettled ? (
                  <div className={styles.loading_state}>
                    <ProgressCircle
                      isIndeterminate
                      aria-label="Loading options"
                    />
                  </div>
                ) : (
                  <div
                    className={styles.empty_message}
                    data-ui="options-empty-state"
                  >
                    {showStartTypingHint && !hasActiveQuery
                      ? startTypingHint
                      : 'No results found'}
                  </div>
                ))}
            </>
          )}
        </div>
      </div>
    </Popover>
  );
};
